import React from "react";
import { LuShieldCheck } from "react-icons/lu";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { useLanguage } from "../context/LanguageContext";
import { useConsent } from "../consent/ConsentContext";

const containerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "18rem",
  borderRadius: "1rem",
};

const center = {
  lat: 48.2082,
  lng: 16.3738,
};

const libraries = ["marker"];
const mapId = (process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "").trim();

const EnabledGoogleMapSection = ({
  locations = [],
  selectedLocationId = null,
  onSelectLocation = null,
}) => {
  const { t } = useLanguage();
  const [authFailure, setAuthFailure] = React.useState(false);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
    libraries,
    preventGoogleFontsLoading: true,
  });

  const [map, setMap] = React.useState(null);

  const enhancedLocations = React.useMemo(
    () =>
      locations.map((loc, index) => ({
        ...loc,
        id: loc.id ?? loc.name ?? `location-${index}`,
      })),
    [locations]
  );
  const hasValidCoords = React.useCallback(
    (coords) =>
      Array.isArray(coords) &&
      coords.length === 2 &&
      coords.every((value) => Number.isFinite(value)),
    []
  );
  const locationsWithCoords = React.useMemo(
    () => enhancedLocations.filter((loc) => hasValidCoords(loc.coords)),
    [enhancedLocations, hasValidCoords]
  );

  const fallbackLocationId = locationsWithCoords[0]?.id ?? enhancedLocations[0]?.id ?? null;
  const activeLocationId = selectedLocationId ?? fallbackLocationId;

  const onLoad = React.useCallback((instance) => setMap(instance), []);
  const onUnmount = React.useCallback(() => setMap(null), []);

  React.useEffect(() => {
    if (map && activeLocationId) {
      const loc = enhancedLocations.find((l) => l.id === activeLocationId);
      if (loc && hasValidCoords(loc.coords)) {
        map.panTo({ lat: loc.coords[0], lng: loc.coords[1] });
      }
    }
  }, [activeLocationId, map, enhancedLocations, hasValidCoords]);

  React.useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => setAuthFailure(true);
    return () => {
      window.gm_authFailure = previous;
    };
  }, []);

  // 🧭 Create SVG marker with custom color
  const createMarkerIcon = (color) => {
    if (!window.google?.maps?.Point) {
      return undefined;
    }
    return {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#fff",
      strokeWeight: 1,
      scale: 1.5,
      anchor: new window.google.maps.Point(12, 24),
    };
  };

  React.useEffect(() => {
    if (!map || !isLoaded || !window.google?.maps) {
      return undefined;
    }

    const { google } = window;
    const hasAdvancedMarkers = Boolean(mapId && google.maps.marker?.AdvancedMarkerElement);

    const markers = locationsWithCoords.map((loc) => {
      const isSelected = activeLocationId === loc.id;
      const color = isSelected ? "#11998e" : "#31326F";
      const position = { lat: loc.coords[0], lng: loc.coords[1] };

      let marker;
      if (hasAdvancedMarkers) {
        const pin = google.maps.marker.PinElement
          ? new google.maps.marker.PinElement({
              background: color,
              borderColor: "#fff",
              glyphColor: "#fff",
            })
          : null;
        marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position,
          content: pin ? pin.element : undefined,
          title: loc.name ?? undefined,
        });
      } else {
        const icon = createMarkerIcon(color);
        marker = new google.maps.Marker({
          map,
          position,
          icon: icon ?? undefined,
        });
      }

      if (onSelectLocation) {
        marker.addListener("click", () => onSelectLocation(loc.id ?? loc.name));
      }

      return marker;
    });

    return () => {
      markers.forEach((marker) => {
        if (!marker) {
          return;
        }

        if (hasAdvancedMarkers && marker instanceof google.maps.marker.AdvancedMarkerElement) {
          marker.map = null;
          return;
        }

        if (typeof marker.setMap === "function") {
          marker.setMap(null);
        }
      });
    };
  }, [map, isLoaded, locationsWithCoords, activeLocationId, onSelectLocation]);

  if (authFailure || loadError) {
    return (
      <div className="map-error">
        {t(
          "googleMap.error.unavailable",
          "Map unavailable. Check the Google Maps API key referrer restrictions."
        )}
      </div>
    );
  }

  if (!googleMapsApiKey) {
    return (
      <div className="map-error">
        {t("googleMap.error.missingKey", "Map API key is missing.")}
      </div>
    );
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        disableDefaultUI: true,
        mapId: mapId || undefined,
        styles: mapId
          ? undefined
          : [
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8dadc" }] },
              { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f1faee" }] },
              { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#1d3557" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
            ],
      }}
    >
    </GoogleMap>
  ) : (
    <div>{t("googleMap.loading", "Loading map...")}</div>
  );
};

const GoogleMapSection = (props) => {
  const { t } = useLanguage();
  const { canUseFunctional, openPreferences } = useConsent();
  const isNightTheme = props?.theme === "night";

  if (!canUseFunctional) {
    return (
      <div
        className={`map-consent-placeholder${isNightTheme ? " map-consent-placeholder--night" : ""}`}
      >
        <button
          type="button"
          className="map-consent-placeholder__button"
          onClick={openPreferences}
        >
          <LuShieldCheck aria-hidden="true" />
          <span>{t("googleMap.consent.cta", "Enable map")}</span>
        </button>
      </div>
    );
  }

  return <EnabledGoogleMapSection {...props} />;
};

export default GoogleMapSection;
